import { ChevronDown, ChevronsUpDown, ChevronUp } from 'lucide-react';
import * as React from 'react';

import type { SkillView } from '@/api/catalog-client';
import {
  useArchiveSkill,
  useAssociableFamilies,
  useCatalogSkills,
  useCreateSkill,
  useExportSkillsMatrixCsv,
  useRestoreSkill,
  useSetSkillFamilies,
  useSkillCampaignUsage,
  useUpdateSkill,
} from '@/api/use-catalog-edition';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { ConfirmDialog } from '@/components/ui/confirm-dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { RichHtmlEditor } from '@/components/rich-html-editor';
import { formatCatalogDate, skillStateLabel } from '@/lib/catalog-labels';
import { cn } from '@/lib/utils';

/**
 * Réduit un fragment HTML riche à son texte visible (S210-SEARCH-RICHTEXT #20) : la recherche doit porter
 * sur le CONTENU du descriptif, jamais sur ses balises. Sans DOM (SSR/robustesse), on retire les balises
 * et on décode quelques entités courantes. Purement indicatif (filtrage client-side).
 */
function stripHtml(html: string): string {
  return html
    .replace(/<[^>]*>/g, ' ')
    .replace(/&nbsp;/g, ' ')
    .replace(/&amp;/g, '&')
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .replace(/\s+/g, ' ')
    .trim();
}

/** REM-A #3 — déclenche l'enregistrement navigateur d'un contenu CSV sous `filename` (parité edition-screen). */
function saveCsv(content: string, filename: string): void {
  const blob = new Blob([content], { type: 'text/csv;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = filename;
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  URL.revokeObjectURL(url);
}

/** Texte d'aide partagé des éditeurs riches de descriptif (WCAG : associé via aria-describedby). */
const DESCRIPTION_HINT =
  'Descriptif enrichi : titres, listes, gras/italique, liens. Le contenu est nettoyé (anti-XSS) à l’enregistrement.';

// =============================================================================
// catalog-skills-screen.tsx — A-25 référentiel de compétences (S9-D4 ; Uc-A-COMPET).
// =============================================================================
// BC catalog, MONDE ADMIN d'ÉCRITURE (P-05). Liste + création + édition + association/
// dissociation de familles + ARCHIVAGE (action DISTINCTE, jamais un delete) + RESTAURATION
// + DUPLICATION d'une SpecificSkill. catalog = référentiel GLOBAL : AUCUNE donnée
// nominative, AUCUNE PII.
//
// Conformité DBU §2.10 (Bloc A) : catégorie contrainte {technologie, solution éditeur,
// certification, autre} ; colonnes « Nombre de familles associées » (chiffre) + « Date de
// dernière modification » (fallback createdAt) ; verrou d'édition sur statut Archivé ;
// « Restaurer » (primitive DÉDIÉE restoreSkill, #16 → DE.SkillRestored) ; « Dupliquer » (pré-remplit le form) ; barre
// d'outils (recherche libellé/code + filtres catégorie/statut/famille + tri colonnes) ; le
// formulaire de création est révélé par le bouton « Créer une compétence ».
//
// État de vue in-memory (React state ; 0 localStorage, RULE_REF_006) — filtrage/tri
// CLIENT-SIDE sur la liste chargée.
//
// L'AUTORITÉ (RBAC, inv.1 unicité perpétuelle du skillCode, inv.3 proposabilité par
// famille, inv.4 archivage = state Archived avec associations préservées + DE.Skill*) est
// l'API ; l'UI relaie l'intention et affiche l'erreur serveur.
//
// WCAG 2.1 AA : <form>/<label> liés ; <select>/<label> liés ; <table> <caption>/<th scope> ;
// en-têtes de tri = <button> + aria-sort ; état rendu en LIBELLÉ texte (badge = renfort) ;
// actions inline avec aria-label explicite ; modales accessibles (ConfirmDialog) ; messages
// role="status"/role="alert" ; navigation clavier native.

/**
 * Catégories contraintes d'une compétence (DBU §2.10 l.2216). `categorie` reste un string
 * libre au contrat API (valeurs héritées possibles) ; l'UI contraint la SAISIE à cette
 * énumération. Une valeur héritée non canonique reste sélectionnable (option d'appoint) pour
 * garder le <select> contrôlé.
 */
const SKILL_CATEGORIES = [
  'technologie',
  'solution éditeur',
  'certification',
  'autre',
] as const;

/** Classe partagée des <select> natifs (tokens sémantiques ; focus visible clavier). */
const SELECT_CLASS = cn(
  'flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm text-foreground',
  'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2',
);

/** Pré-remplissage du formulaire de création issu d'une duplication (DBU §2.10 l.2238). */
interface SkillFormPrefill {
  readonly libelle: string;
  readonly categorie: string;
  readonly familles: readonly string[];
}

/** Colonnes triables du catalogue (DBU §2.10 l.2272 : tri sur l'ensemble des colonnes). */
type SkillSortColumn =
  | 'code'
  | 'libelle'
  | 'categorie'
  | 'famillesCount'
  | 'state'
  | 'date';
type SortDirection = 'asc' | 'desc';
type StatutFilter = 'all' | 'Active' | 'Archived';

interface SkillsView {
  readonly q: string;
  readonly categorie: string;
  readonly statut: StatutFilter;
  readonly famille: string;
  readonly sortColumn: SkillSortColumn;
  readonly sortDirection: SortDirection;
}

const INITIAL_VIEW: SkillsView = {
  q: '',
  categorie: 'all',
  statut: 'all',
  famille: 'all',
  sortColumn: 'libelle',
  sortDirection: 'asc',
};

function compareSkills(
  a: SkillView,
  b: SkillView,
  column: SkillSortColumn,
): number {
  switch (column) {
    case 'code':
      return a.skillCode.localeCompare(b.skillCode, 'fr');
    case 'libelle':
      return a.libelle.localeCompare(b.libelle, 'fr');
    case 'categorie':
      return a.categorie.localeCompare(b.categorie, 'fr');
    case 'famillesCount':
      return a.familles.length - b.familles.length;
    case 'state':
      return a.state.localeCompare(b.state, 'fr');
    case 'date':
      // « Date de dernière modification » (#24) = updatedAt véridique (F0-DB-4), plus createdAt.
      // ISO 8601 : l'ordre lexicographique coïncide avec l'ordre chronologique.
      return a.updatedAt.localeCompare(b.updatedAt);
  }
}

export function CatalogSkillsScreen(): React.JSX.Element {
  return (
    <div className="space-y-8">
      <header className="space-y-1">
        <h1 className="text-2xl font-semibold tracking-tight">
          Référentiel de compétences
        </h1>
        <p className="text-muted-foreground">
          Référentiel global des compétences spécifiques. Créez, modifiez, associez des
          familles, dupliquez, archivez ou restaurez une compétence. L'archivage est
          réversible côté serveur (aucune suppression).
        </p>
      </header>

      <SkillsManager />
    </div>
  );
}

/**
 * Conteneur A-25 : détient la liste chargée, l'état de vue (recherche/filtres/tri) et
 * l'état d'ouverture + pré-remplissage du formulaire de création. Filtrage/tri CLIENT-SIDE.
 */
/** Sous-modules du référentiel (DBU §2.10 l.2200 : « deux sous-modules en onglets Catalogue/Matrice »). */
type SkillsTab = 'catalogue' | 'matrice';

function SkillsManager(): React.JSX.Element {
  const list = useCatalogSkills();
  const [view, setView] = React.useState<SkillsView>(INITIAL_VIEW);
  // #27 — navigation par ONGLETS entre le catalogue (liste/CRUD) et la matrice compétences×familles.
  const [tab, setTab] = React.useState<SkillsTab>('catalogue');

  // Formulaire de création révélé (DBU §2.10 l.2208 : bouton « Créer une compétence »).
  const [formOpen, setFormOpen] = React.useState(false);
  const [prefill, setPrefill] = React.useState<SkillFormPrefill | null>(null);
  // Nonce de remontage : chaque ouverture (création vierge ou duplication) réinitialise le
  // form avec ses valeurs initiales via `key`.
  const [formNonce, setFormNonce] = React.useState(0);

  const rows = React.useMemo(() => list.data ?? [], [list.data]);

  const openCreate = React.useCallback((): void => {
    setPrefill(null);
    setFormOpen(true);
    setFormNonce((n) => n + 1);
  }, []);

  const openDuplicate = React.useCallback((skill: SkillView): void => {
    setPrefill({
      libelle: `${skill.libelle} (copie)`,
      categorie: skill.categorie,
      familles: skill.familles,
    });
    setFormOpen(true);
    setFormNonce((n) => n + 1);
  }, []);

  // Options de filtre dérivées des rows chargées (sans fetch supplémentaire).
  const categorieOptions = React.useMemo(() => {
    const seen = new Set<string>();
    for (const row of rows) {
      if (row.categorie.trim().length > 0) {
        seen.add(row.categorie);
      }
    }
    return [...seen].sort((a, b) => a.localeCompare(b, 'fr'));
  }, [rows]);

  const familleOptions = React.useMemo(() => {
    const seen = new Set<string>();
    for (const row of rows) {
      for (const f of row.familles) {
        seen.add(f);
      }
    }
    return [...seen].sort((a, b) => a.localeCompare(b, 'fr'));
  }, [rows]);

  const filteredSorted = React.useMemo(() => {
    const needle = view.q.trim().toLowerCase();
    const filtered = rows.filter((row) => {
      if (view.statut !== 'all' && row.state !== view.statut) {
        return false;
      }
      if (view.categorie !== 'all' && row.categorie !== view.categorie) {
        return false;
      }
      if (view.famille !== 'all' && !row.familles.includes(view.famille)) {
        return false;
      }
      if (needle !== '') {
        // #20 — la recherche porte AUSSI sur le descriptif (texte, balises retirées), plus seulement
        // libellé+code. Un mot présent UNIQUEMENT dans le descriptif est désormais trouvable (DoD-R3).
        const haystack =
          `${row.libelle} ${row.skillCode} ${stripHtml(row.description)}`.toLowerCase();
        if (!haystack.includes(needle)) {
          return false;
        }
      }
      return true;
    });
    return [...filtered].sort((a, b) => {
      const base = compareSkills(a, b, view.sortColumn);
      return view.sortDirection === 'asc' ? base : -base;
    });
  }, [rows, view]);

  const onSort = React.useCallback((column: SkillSortColumn): void => {
    setView((prev) => ({
      ...prev,
      sortColumn: column,
      sortDirection:
        prev.sortColumn === column && prev.sortDirection === 'asc' ? 'desc' : 'asc',
    }));
  }, []);

  if (list.isPending) {
    return (
      <p role="status" className="text-muted-foreground">
        Chargement des compétences…
      </p>
    );
  }
  if (list.isError) {
    return (
      <p role="alert" className="text-destructive">
        Erreur de chargement : {list.error.message}
      </p>
    );
  }

  return (
    <div className="space-y-6">
      {/* Onglets Catalogue / Matrice (DBU §2.10 l.2200) — deux sous-modules du référentiel. */}
      <SkillsTabs tab={tab} onSelect={setTab} />

      {tab === 'matrice' ? (
        <div
          role="tabpanel"
          id="skills-panel-matrice"
          aria-labelledby="skills-tab-matrice"
        >
          <SkillMatrix skills={rows} />
        </div>
      ) : (
        <div
          role="tabpanel"
          id="skills-panel-catalogue"
          aria-labelledby="skills-tab-catalogue"
          className="space-y-6"
        >
      {/* Barre d'outils unifiée (DBU §2.10 l.2208). */}
      <div
        role="search"
        aria-label="Recherche et filtres des compétences"
        className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4"
      >
        <div className="flex flex-col gap-1.5">
          <Label htmlFor="skills-search">Rechercher (libellé ou code)</Label>
          <Input
            id="skills-search"
            type="search"
            value={view.q}
            placeholder="Ex. Python ou SK-PY-001"
            onChange={(e) => setView((prev) => ({ ...prev, q: e.target.value }))}
          />
        </div>
        <div className="flex flex-col gap-1.5">
          <Label htmlFor="skills-filter-categorie">Catégorie</Label>
          <select
            id="skills-filter-categorie"
            className={SELECT_CLASS}
            value={view.categorie}
            onChange={(e) =>
              setView((prev) => ({ ...prev, categorie: e.target.value }))
            }
          >
            <option value="all">Toutes les catégories</option>
            {categorieOptions.map((c) => (
              <option key={c} value={c}>
                {c}
              </option>
            ))}
          </select>
        </div>
        <div className="flex flex-col gap-1.5">
          <Label htmlFor="skills-filter-statut">Statut</Label>
          <select
            id="skills-filter-statut"
            className={SELECT_CLASS}
            value={view.statut}
            onChange={(e) =>
              setView((prev) => ({
                ...prev,
                statut: e.target.value as StatutFilter,
              }))
            }
          >
            <option value="all">Tous les statuts</option>
            <option value="Active">Active</option>
            <option value="Archived">Archivée</option>
          </select>
        </div>
        <div className="flex flex-col gap-1.5">
          <Label htmlFor="skills-filter-famille">Famille associée</Label>
          <select
            id="skills-filter-famille"
            className={SELECT_CLASS}
            value={view.famille}
            onChange={(e) =>
              setView((prev) => ({ ...prev, famille: e.target.value }))
            }
          >
            <option value="all">Toutes les familles</option>
            {familleOptions.map((f) => (
              <option key={f} value={f}>
                {f}
              </option>
            ))}
          </select>
        </div>
      </div>

      <div>
        <Button
          type="button"
          onClick={openCreate}
          aria-expanded={formOpen}
          aria-controls="create-skill-form"
        >
          Créer une compétence
        </Button>
      </div>

      {formOpen ? (
        <CreateSkillForm
          key={formNonce}
          id="create-skill-form"
          initial={prefill}
          onClose={() => setFormOpen(false)}
        />
      ) : null}

      <SkillsTable
        skills={filteredSorted}
        totalCount={rows.length}
        view={view}
        onSort={onSort}
        onDuplicate={openDuplicate}
      />
        </div>
      )}
    </div>
  );
}

/**
 * Barre d'onglets accessible (WCAG 2.1 : role="tablist" / role="tab", roving tabindex, flèches
 * gauche/droite + Home/End). Deux sous-modules (DBU §2.10 l.2200) : Catalogue (liste/CRUD) et Matrice
 * (compétences × familles). Chaque onglet contrôle son tabpanel via aria-controls.
 */
function SkillsTabs({
  tab,
  onSelect,
}: {
  readonly tab: SkillsTab;
  readonly onSelect: (tab: SkillsTab) => void;
}): React.JSX.Element {
  const order: readonly SkillsTab[] = ['catalogue', 'matrice'];
  const labels: Record<SkillsTab, string> = {
    catalogue: 'Catalogue',
    matrice: 'Matrice',
  };

  // Roving tabindex (WCAG APG) : la navigation clavier est portée par les onglets EUX-MÊMES (flèches
  // gauche/droite + Home/End), pas par le conteneur tablist (qui reste non focusable). Le focus SUIT
  // la sélection.
  const onTabKeyDown = (event: React.KeyboardEvent<HTMLButtonElement>): void => {
    const current = order.indexOf(tab);
    let next = current;
    if (event.key === 'ArrowRight') {
      next = (current + 1) % order.length;
    } else if (event.key === 'ArrowLeft') {
      next = (current - 1 + order.length) % order.length;
    } else if (event.key === 'Home') {
      next = 0;
    } else if (event.key === 'End') {
      next = order.length - 1;
    } else {
      return;
    }
    event.preventDefault();
    onSelect(order[next]!);
    const tabs = event.currentTarget.parentElement?.querySelectorAll<HTMLButtonElement>(
      '[role="tab"]',
    );
    tabs?.[next]?.focus();
  };

  return (
    <div
      role="tablist"
      aria-label="Sous-modules du référentiel de compétences"
      className="flex gap-1 border-b border-border"
    >
      {order.map((key) => {
        const active = tab === key;
        return (
          <button
            key={key}
            type="button"
            role="tab"
            id={`skills-tab-${key}`}
            aria-selected={active}
            aria-controls={`skills-panel-${key}`}
            tabIndex={active ? 0 : -1}
            onClick={() => onSelect(key)}
            onKeyDown={onTabKeyDown}
            className={cn(
              '-mb-px border-b-2 px-4 py-2 text-sm font-medium transition-colors',
              'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2',
              active
                ? 'border-primary text-foreground'
                : 'border-transparent text-muted-foreground hover:text-foreground',
            )}
          >
            {labels[key]}
          </button>
        );
      })}
    </div>
  );
}

/**
 * Vue MATRICE compétences × familles (DBU §2.10 l.2206/2230). Lignes = compétences, colonnes = familles
 * (univers = union des familles associées, sans API nouvelle). Cocher une case ASSOCIE la compétence à la
 * famille (setFamilies), décocher DISSOCIE. Les compétences ARCHIVÉES ne sont pas éditables (garde #4 API).
 *
 * DoD-R3 : cocher une case CRÉE réellement l'association (assertion qui échoue — la matrice n'existait pas).
 */
function SkillMatrix({
  skills,
}: {
  readonly skills: readonly SkillView[];
}): React.JSX.Element {
  const setFamilies = useSetSkillFamilies();
  const exportMatrix = useExportSkillsMatrixCsv();
  const [pendingCell, setPendingCell] = React.useState<string | null>(null);

  const onExportCsv = React.useCallback((): void => {
    // REM-A #3 : le CONTENU CSV (déjà durci côté API) est récupéré puis enregistré par le navigateur.
    exportMatrix.mutate(undefined, {
      onSuccess: (csv) => saveCsv(csv, 'matrice-associations-competences-familles.csv'),
    });
  }, [exportMatrix]);

  const familles = React.useMemo(() => {
    const seen = new Set<string>();
    for (const s of skills) {
      for (const f of s.familles) {
        seen.add(f);
      }
    }
    return [...seen].sort((a, b) => a.localeCompare(b, 'fr'));
  }, [skills]);

  const toggle = React.useCallback(
    (skill: SkillView, famille: string): void => {
      const has = skill.familles.includes(famille);
      const next = has
        ? skill.familles.filter((f) => f !== famille)
        : [...skill.familles, famille];
      const cellKey = `${skill.skillCode}|${famille}`;
      setPendingCell(cellKey);
      setFamilies.mutate(
        { skillCode: skill.skillCode, familles: next },
        { onSettled: () => setPendingCell((c) => (c === cellKey ? null : c)) },
      );
    },
    [setFamilies],
  );

  if (skills.length === 0) {
    return (
      <p role="status" className="text-muted-foreground">
        Aucune compétence référencée pour le moment.
      </p>
    );
  }
  if (familles.length === 0) {
    return (
      <p role="status" className="text-muted-foreground">
        Aucune famille associée pour l’instant. Associez une famille à une compétence depuis
        l’onglet Catalogue pour peupler la matrice.
      </p>
    );
  }

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="space-y-1">
          <h2 className="text-lg font-semibold tracking-tight">
            Matrice compétences × familles
          </h2>
          <p className="text-sm text-muted-foreground">
            Cochez une case pour associer une compétence à une famille, décochez pour la dissocier.
            Les compétences archivées ne sont pas modifiables.
          </p>
        </div>
        {/* REM-A #3 (DBU §2.10 l.2274) — export CSV de la MATRICE d'associations (contenu durci côté API). */}
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={onExportCsv}
          disabled={exportMatrix.isPending}
        >
          {exportMatrix.isPending ? 'Export…' : 'Exporter la matrice (CSV)'}
        </Button>
      </div>

      <div aria-live="polite" aria-atomic="true">
        {setFamilies.isError ? (
          <p role="alert" className="text-sm text-destructive">
            {setFamilies.error.message}
          </p>
        ) : null}
        {exportMatrix.isError ? (
          <p role="alert" className="text-sm text-destructive">
            Échec de l’export de la matrice. {exportMatrix.error.message}
          </p>
        ) : null}
      </div>

      <div className="overflow-x-auto rounded-lg border border-border">
        <table className="ds-table w-full border-collapse text-sm">
          <caption className="sr-only">
            Matrice des associations compétence × famille (cases cochables)
          </caption>
          <thead>
            <tr className="border-b border-border bg-muted/40 text-left text-muted-foreground">
              <th scope="col" className="px-4 py-3 font-medium">
                Compétence
              </th>
              {familles.map((f) => (
                <th key={f} scope="col" className="px-3 py-3 text-center font-medium">
                  {f}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {skills.map((skill) => {
              const isArchived = skill.state === 'Archived';
              return (
                <tr
                  key={skill.skillCode}
                  className="border-b border-border last:border-b-0"
                >
                  <th scope="row" className="px-4 py-2 text-left font-normal">
                    <span className="font-mono text-xs text-muted-foreground">
                      {skill.skillCode}
                    </span>{' '}
                    <span>{skill.libelle}</span>
                    {isArchived ? (
                      <span className="ml-1 text-xs italic text-muted-foreground">
                        (archivée)
                      </span>
                    ) : null}
                  </th>
                  {familles.map((f) => {
                    const checked = skill.familles.includes(f);
                    const cellKey = `${skill.skillCode}|${f}`;
                    return (
                      <td key={f} className="px-3 py-2 text-center">
                        <input
                          type="checkbox"
                          checked={checked}
                          disabled={
                            isArchived ||
                            (setFamilies.isPending && pendingCell === cellKey)
                          }
                          onChange={() => toggle(skill, f)}
                          aria-label={`${checked ? 'Dissocier' : 'Associer'} la compétence ${skill.libelle} et la famille ${f}`}
                          className={cn(
                            'h-4 w-4 cursor-pointer accent-primary',
                            'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2',
                            'disabled:cursor-not-allowed disabled:opacity-50',
                          )}
                        />
                      </td>
                    );
                  })}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

/** <select> de catégorie contraint aux 4 valeurs canoniques (+ appoint si valeur héritée). */
function CategorySelect({
  id,
  value,
  onChange,
}: {
  readonly id: string;
  readonly value: string;
  readonly onChange: (value: string) => void;
}): React.JSX.Element {
  const isCanonical = (SKILL_CATEGORIES as readonly string[]).includes(value);
  return (
    <select
      id={id}
      name="categorie"
      className={SELECT_CLASS}
      value={value}
      onChange={(e) => onChange(e.target.value)}
    >
      {SKILL_CATEGORIES.map((c) => (
        <option key={c} value={c}>
          {c}
        </option>
      ))}
      {!isCanonical && value.length > 0 ? (
        <option value={value}>{value} (hérité)</option>
      ) : null}
    </select>
  );
}

/**
 * REM-A #1 (DBU §2.10) — SÉLECTEUR CONTRAINT d'association Compétence↔Familles. Les familles proposées
 * proviennent EXCLUSIVEMENT du référentiel des familles de profils (DISTINCT `Profile.famille`) : l'admin
 * COCHE, il ne saisit jamais (intégrité référentielle — une association ne peut pointer que vers une famille
 * existante, condition de la jointure de déclarabilité fournisseur `Profile.famille === SkillFamilyAssoc.famille`).
 *
 * L'univers affiché = union du référentiel et des familles DÉJÀ sélectionnées (duplication : une famille copiée
 * reste visible même si le référentiel évoluait). WCAG 2.1 AA : <fieldset>/<legend>, chaque case labellisée
 * (label associé par `htmlFor`), état porté par l'attribut natif `checked` (jamais une couleur seule).
 */
function FamilySelector({
  referential,
  selected,
  onToggle,
  isLoading,
  isError,
  errorMessage,
}: {
  readonly referential: readonly string[];
  readonly selected: readonly string[];
  readonly onToggle: (famille: string, next: boolean) => void;
  readonly isLoading: boolean;
  readonly isError: boolean;
  readonly errorMessage?: string | undefined;
}): React.JSX.Element {
  const selectedSet = React.useMemo(() => new Set(selected), [selected]);
  // Univers = référentiel ∪ familles déjà cochées (ne jamais faire disparaître une sélection en cours), trié fr.
  const options = React.useMemo(
    () =>
      [...new Set([...referential, ...selected])].sort((a, b) =>
        a.localeCompare(b, 'fr'),
      ),
    [referential, selected],
  );

  return (
    <fieldset className="space-y-2 rounded-md border border-border p-3">
      <legend className="px-1 text-sm font-medium text-foreground">
        Familles de profils associées
      </legend>
      <p className="text-xs text-muted-foreground">
        Sélection contrainte au référentiel des familles de profils : cochez les familles qui
        pourront proposer cette compétence en saisie fournisseur.
      </p>
      {isError ? (
        <p role="alert" className="text-sm text-destructive">
          Impossible de charger le référentiel des familles.
          {errorMessage !== undefined ? ` ${errorMessage}` : ''}
        </p>
      ) : isLoading ? (
        <p role="status" className="text-sm text-muted-foreground">
          Chargement du référentiel des familles…
        </p>
      ) : options.length === 0 ? (
        <p role="status" className="text-sm text-muted-foreground">
          Aucune famille de profils au référentiel. Créez d’abord des profils pour définir des
          familles associables.
        </p>
      ) : (
        <div className="flex flex-col gap-2">
          {options.map((famille) => {
            const inputId = `skill-famille-${famille}`.replace(
              /[^a-zA-Z0-9_-]/g,
              '_',
            );
            return (
              <div key={famille} className="flex items-center gap-2 text-sm text-foreground">
                <input
                  id={inputId}
                  type="checkbox"
                  name="familles"
                  value={famille}
                  checked={selectedSet.has(famille)}
                  onChange={(e) => onToggle(famille, e.target.checked)}
                  className="h-4 w-4 rounded border-input focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                />
                <label htmlFor={inputId}>{famille}</label>
              </div>
            );
          })}
        </div>
      )}
    </fieldset>
  );
}

function CreateSkillForm({
  id,
  initial,
  onClose,
}: {
  readonly id: string;
  readonly initial: SkillFormPrefill | null;
  readonly onClose: () => void;
}): React.JSX.Element {
  const create = useCreateSkill();
  // REM-A #1 : référentiel des familles de profils associables (SÉLECTEUR contraint, vs texte libre).
  const familiesQuery = useAssociableFamilies();
  const [skillCode, setSkillCode] = React.useState('');
  const [libelle, setLibelle] = React.useState(initial?.libelle ?? '');
  const [categorie, setCategorie] = React.useState(
    initial?.categorie ?? SKILL_CATEGORIES[0],
  );
  const [description, setDescription] = React.useState('');
  // Familles SÉLECTIONNÉES (jamais saisies) : ensemble contraint au référentiel. Duplication ⇒ pré-cochage.
  const [selectedFamilies, setSelectedFamilies] = React.useState<readonly string[]>(
    initial?.familles ?? [],
  );
  const [createdCode, setCreatedCode] = React.useState<string | null>(null);

  const toggleFamily = React.useCallback((famille: string, next: boolean): void => {
    setSelectedFamilies((prev) =>
      next ? [...new Set([...prev, famille])] : prev.filter((f) => f !== famille),
    );
  }, []);

  const onSubmit = React.useCallback(
    (event: React.FormEvent<HTMLFormElement>): void => {
      event.preventDefault();
      // Les familles proviennent EXCLUSIVEMENT de la sélection contrainte (aucun parsing de texte libre).
      const familles = [...selectedFamilies];
      create.mutate(
        { skillCode, libelle, description, categorie, familles },
        {
          onSuccess: (skill) => {
            setCreatedCode(skill.skillCode);
            setSkillCode('');
            setLibelle('');
            setCategorie(SKILL_CATEGORIES[0]);
            setDescription('');
            setSelectedFamilies([]);
          },
        },
      );
    },
    [create, skillCode, libelle, description, categorie, selectedFamilies],
  );

  return (
    <Card id={id}>
      <CardHeader>
        <CardTitle className="text-base">
          {initial !== null ? 'Dupliquer une compétence' : 'Créer une compétence'}
        </CardTitle>
        <CardDescription>
          Le code de compétence est unique et perpétuel (vérifié par le serveur). Les
          familles associées se choisissent dans le référentiel des familles de profils
          (sélection contrainte, aucune saisie libre).
          {initial !== null
            ? ' Le libellé et les familles ont été copiés ; complétez un nouveau code.'
            : ''}
        </CardDescription>
      </CardHeader>
      <CardContent>
        <form onSubmit={onSubmit} className="space-y-4" aria-label="Créer une compétence">
          <div className="grid gap-4 sm:grid-cols-2">
            <div className="space-y-2">
              <Label htmlFor="skill-code">Code</Label>
              <Input
                id="skill-code"
                name="skillCode"
                className="font-mono"
                value={skillCode}
                onChange={(e) => setSkillCode(e.target.value)}
                required
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="skill-libelle">Libellé</Label>
              <Input
                id="skill-libelle"
                name="libelle"
                value={libelle}
                onChange={(e) => setLibelle(e.target.value)}
                required
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="skill-categorie">Catégorie</Label>
              <CategorySelect
                id="skill-categorie"
                value={categorie}
                onChange={setCategorie}
              />
            </div>
          </div>
          {/* REM-A #1 (DBU §2.10) — SÉLECTEUR CONTRAINT des familles de profils associées (vs texte libre). */}
          <FamilySelector
            referential={familiesQuery.data ?? []}
            selected={selectedFamilies}
            onToggle={toggleFamily}
            isLoading={familiesQuery.isLoading}
            isError={familiesQuery.isError}
            errorMessage={familiesQuery.error?.message}
          />
          {/* #2 — descriptif ENRICHI (texte riche) via l'éditeur WYSIWYG PARTAGÉ (0 dépendance, sanitizé). */}
          <RichHtmlEditor
            id="skill-description"
            label="Description"
            hint={DESCRIPTION_HINT}
            value={description}
            onChange={setDescription}
          />

          <div aria-live="polite" aria-atomic="true">
            {create.isError ? (
              <p role="alert" className="text-sm text-destructive">
                {create.error.message}
              </p>
            ) : null}
            {createdCode !== null && !create.isError ? (
              <p role="status" className="text-sm text-success">
                Compétence créée : {createdCode}.
              </p>
            ) : null}
          </div>

          <div className="flex gap-2">
            <Button type="submit" disabled={create.isPending}>
              {create.isPending ? 'Création…' : 'Créer la compétence'}
            </Button>
            <Button type="button" variant="ghost" onClick={onClose}>
              Fermer
            </Button>
          </div>
        </form>
      </CardContent>
    </Card>
  );
}

function SkillsTable({
  skills,
  totalCount,
  view,
  onSort,
  onDuplicate,
}: {
  readonly skills: readonly SkillView[];
  readonly totalCount: number;
  readonly view: SkillsView;
  readonly onSort: (column: SkillSortColumn) => void;
  readonly onDuplicate: (skill: SkillView) => void;
}): React.JSX.Element {
  if (totalCount === 0) {
    return (
      <p role="status" className="text-muted-foreground">
        Aucune compétence référencée pour le moment.
      </p>
    );
  }

  return (
    <div className="space-y-3">
      <h2 className="text-lg font-semibold tracking-tight">Compétences existantes</h2>
      {skills.length === 0 ? (
        <p role="status" className="text-muted-foreground">
          Aucune compétence ne correspond aux critères de recherche.
        </p>
      ) : (
        <div className="overflow-x-auto rounded-lg border border-border">
          <table className="ds-table w-full border-collapse text-sm">
            <caption className="sr-only">
              Compétences (code, libellé, catégorie, nombre de familles associées, état,
              date de dernière modification, actions)
            </caption>
            <thead>
              <tr className="border-b border-border bg-muted/40 text-left text-muted-foreground">
                <SortableHeader
                  column="code"
                  label="Code"
                  view={view}
                  onSort={onSort}
                />
                <SortableHeader
                  column="libelle"
                  label="Libellé"
                  view={view}
                  onSort={onSort}
                />
                <SortableHeader
                  column="categorie"
                  label="Catégorie"
                  view={view}
                  onSort={onSort}
                />
                <SortableHeader
                  column="famillesCount"
                  label="Nombre de familles associées"
                  view={view}
                  onSort={onSort}
                />
                <SortableHeader
                  column="state"
                  label="État"
                  view={view}
                  onSort={onSort}
                />
                <SortableHeader
                  column="date"
                  label="Date de dernière modification"
                  view={view}
                  onSort={onSort}
                />
                <th scope="col" className="px-4 py-3 font-medium">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody>
              {skills.map((skill) => (
                <SkillRow
                  key={skill.skillCode}
                  skill={skill}
                  onDuplicate={onDuplicate}
                />
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

/** En-tête de colonne triable : <th aria-sort> + <button> déclencheur (clavier natif). */
function SortableHeader({
  column,
  label,
  view,
  onSort,
}: {
  readonly column: SkillSortColumn;
  readonly label: string;
  readonly view: SkillsView;
  readonly onSort: (column: SkillSortColumn) => void;
}): React.JSX.Element {
  const active = view.sortColumn === column;
  const ariaSort: React.AriaAttributes['aria-sort'] = active
    ? view.sortDirection === 'asc'
      ? 'ascending'
      : 'descending'
    : 'none';
  const Icon = !active
    ? ChevronsUpDown
    : view.sortDirection === 'asc'
      ? ChevronUp
      : ChevronDown;
  return (
    <th scope="col" aria-sort={ariaSort} className="px-4 py-3 font-medium">
      <button
        type="button"
        onClick={() => onSort(column)}
        className={cn(
          'inline-flex items-center gap-1 rounded-sm text-left font-medium',
          'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2',
        )}
      >
        <span>{label}</span>
        <Icon aria-hidden="true" className="h-3.5 w-3.5 shrink-0" />
      </button>
    </th>
  );
}

function SkillRow({
  skill,
  onDuplicate,
}: {
  readonly skill: SkillView;
  readonly onDuplicate: (skill: SkillView) => void;
}): React.JSX.Element {
  const update = useUpdateSkill();
  const setFamilies = useSetSkillFamilies();
  const archive = useArchiveSkill();
  // #16 — primitive DÉDIÉE (parité restoreProfile) : « Restaurer » NE passe PLUS par useUpdateSkill
  // ({state:'Active'} y était un NO-OP côté serveur — le skill restait Archivé). Cette mutation appelle
  // POST .../restore, qui bascule RÉELLEMENT Archivé → Actif et émet DE.SkillRestored.
  const restore = useRestoreSkill();
  const [open, setOpen] = React.useState(false);
  const [confirmArchive, setConfirmArchive] = React.useState(false);
  const [confirmRestore, setConfirmRestore] = React.useState(false);

  // R15-CAT-03 (DBU §2.10 l.2250 amendée DEC-27) — avertissement d'archivage INFORMATIONNEL : campagnes EN
  // COURS où la compétence est PROPOSABLE. Piloté par la modale (`confirmArchive`) : la requête n'est émise
  // qu'à l'ouverture de la confirmation d'archivage. N'affecte JAMAIS le bouton (impact non rétroactif : une
  // campagne fige sa version de catalogue). Miroir EXACT du parcours d'archivage profil.
  const campaignUsage = useSkillCampaignUsage(confirmArchive ? skill.skillCode : null);
  const campaignUsageCount = campaignUsage.data?.length ?? 0;

  const [libelle, setLibelle] = React.useState(skill.libelle);
  const [categorie, setCategorie] = React.useState(skill.categorie);
  const [description, setDescription] = React.useState(skill.description);
  const [famillesText, setFamillesText] = React.useState(skill.familles.join(', '));

  const state = skillStateLabel(skill.state);
  const isArchived = skill.state === 'Archived';

  const onSaveDetails = React.useCallback((): void => {
    update.mutate({
      skillCode: skill.skillCode,
      input: { libelle, categorie, description },
    });
  }, [update, skill.skillCode, libelle, categorie, description]);

  const onSaveFamilies = React.useCallback((): void => {
    const familles = famillesText
      .split(',')
      .map((f) => f.trim())
      .filter((f) => f.length > 0);
    setFamilies.mutate({ skillCode: skill.skillCode, familles });
  }, [setFamilies, skill.skillCode, famillesText]);

  const onArchive = React.useCallback((): void => {
    archive.mutate(skill.skillCode, {
      onSettled: () => setConfirmArchive(false),
    });
  }, [archive, skill.skillCode]);

  // « Restaurer » (DBU §2.10 l.2256, #16) : primitive DÉDIÉE POST .../restore (Archivé → Actif RÉEL +
  // DE.SkillRestored), jamais un updateSkill({state}) — ce dernier ignore `state` (NO-OP, le bug corrigé).
  const onRestore = React.useCallback((): void => {
    restore.mutate(skill.skillCode, {
      onSettled: () => setConfirmRestore(false),
    });
  }, [restore, skill.skillCode]);

  return (
    <>
      <tr className="border-b border-border last:border-b-0 align-top">
        <td className="px-4 py-3 font-mono">{skill.skillCode}</td>
        <td className="px-4 py-3">{skill.libelle}</td>
        <td className="px-4 py-3">{skill.categorie}</td>
        <td className="px-4 py-3 tabular-nums">{skill.familles.length}</td>
        <td className="px-4 py-3">
          {/* Badge = renfort ; le LIBELLÉ texte porte l'information (WCAG). */}
          <Badge variant={state.variant}>{state.label}</Badge>
        </td>
        {/* #24 — « Date de dernière modification » = updatedAt VÉRIDIQUE (F0-DB-4), plus le fallback createdAt. */}
        <td className="px-4 py-3">{formatCatalogDate(skill.updatedAt)}</td>
        <td className="px-4 py-3">
          <div className="flex flex-wrap gap-2">
            {/* Verrou d'édition sur statut Archivé (DBU §2.10 l.2222) : « Modifier »
                MASQUÉ ; seule la restauration est offerte. */}
            {isArchived ? (
              <Button
                type="button"
                variant="outline"
                size="sm"
                disabled={restore.isPending}
                aria-label={`Restaurer la compétence ${skill.skillCode}`}
                onClick={() => setConfirmRestore(true)}
              >
                Restaurer
              </Button>
            ) : (
              <>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  aria-expanded={open}
                  aria-controls={`skill-edit-${skill.skillCode}`}
                  aria-label={`Modifier la compétence ${skill.skillCode}`}
                  onClick={() => setOpen((v) => !v)}
                >
                  {open ? 'Fermer' : 'Modifier'}
                </Button>
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  aria-label={`Dupliquer la compétence ${skill.skillCode}`}
                  onClick={() => onDuplicate(skill)}
                >
                  Dupliquer
                </Button>
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  disabled={archive.isPending}
                  aria-label={`Archiver la compétence ${skill.skillCode}`}
                  onClick={() => setConfirmArchive(true)}
                >
                  Archiver
                </Button>
              </>
            )}
          </div>
        </td>
      </tr>

      {open && !isArchived ? (
        <tr id={`skill-edit-${skill.skillCode}`}>
          <td colSpan={7} className="bg-muted/20 px-4 py-4">
            <div className="grid gap-6 md:grid-cols-2">
              {/* Édition contenu (PATCH). */}
              <div className="space-y-3">
                <h3 className="text-sm font-medium text-foreground">
                  Détails de la compétence
                </h3>
                <div className="space-y-2">
                  <Label htmlFor={`edit-libelle-${skill.skillCode}`}>Libellé</Label>
                  <Input
                    id={`edit-libelle-${skill.skillCode}`}
                    value={libelle}
                    onChange={(e) => setLibelle(e.target.value)}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor={`edit-categorie-${skill.skillCode}`}>Catégorie</Label>
                  <CategorySelect
                    id={`edit-categorie-${skill.skillCode}`}
                    value={categorie}
                    onChange={setCategorie}
                  />
                </div>
                {/* #2 — descriptif ENRICHI (texte riche) via l'éditeur WYSIWYG PARTAGÉ (sanitizé). */}
                <RichHtmlEditor
                  id={`edit-description-${skill.skillCode}`}
                  label="Description"
                  hint={DESCRIPTION_HINT}
                  value={description}
                  onChange={setDescription}
                />
                <div aria-live="polite" aria-atomic="true">
                  {update.isError ? (
                    <p role="alert" className="text-sm text-destructive">
                      {update.error.message}
                    </p>
                  ) : null}
                  {update.isSuccess ? (
                    <p role="status" className="text-sm text-success">
                      Détails enregistrés.
                    </p>
                  ) : null}
                </div>
                <Button
                  type="button"
                  size="sm"
                  onClick={onSaveDetails}
                  disabled={update.isPending}
                >
                  {update.isPending ? 'Enregistrement…' : 'Enregistrer les détails'}
                </Button>

                {/* #25 — journal des modifications au volet détail : date de dernière modif VÉRIDIQUE
                    (updatedAt, F0-DB-4). Chaque édition de contenu est journalisée à l'audit (DE.SkillContentEdited). */}
                <div className="rounded-md border border-border bg-background p-3">
                  <h4 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                    Journal des modifications
                  </h4>
                  <p className="mt-1 text-sm text-foreground">
                    Dernière modification :{' '}
                    <time dateTime={skill.updatedAt}>
                      {formatCatalogDate(skill.updatedAt)}
                    </time>
                  </p>
                  <p className="mt-1 text-xs text-muted-foreground">
                    Chaque édition du contenu (libellé, description, catégorie) est horodatée et
                    journalisée à l’audit.
                  </p>
                </div>
              </div>

              {/* Familles associées (PUT, associer/dissocier). */}
              <div className="space-y-3">
                <h3 className="text-sm font-medium text-foreground">
                  Familles associées
                </h3>
                <div className="space-y-2">
                  <Label htmlFor={`edit-familles-${skill.skillCode}`}>
                    Familles (séparées par des virgules)
                  </Label>
                  <Input
                    id={`edit-familles-${skill.skillCode}`}
                    value={famillesText}
                    onChange={(e) => setFamillesText(e.target.value)}
                  />
                </div>
                <p className="text-xs text-muted-foreground">
                  L'ensemble saisi REMPLACE les familles associées (les codes retirés
                  sont dissociés, les nouveaux sont associés).
                </p>
                <div aria-live="polite" aria-atomic="true">
                  {setFamilies.isError ? (
                    <p role="alert" className="text-sm text-destructive">
                      {setFamilies.error.message}
                    </p>
                  ) : null}
                  {setFamilies.isSuccess ? (
                    <p role="status" className="text-sm text-success">
                      Familles mises à jour.
                    </p>
                  ) : null}
                </div>
                <Button
                  type="button"
                  size="sm"
                  variant="secondary"
                  onClick={onSaveFamilies}
                  disabled={setFamilies.isPending}
                >
                  {setFamilies.isPending
                    ? 'Enregistrement…'
                    : 'Mettre à jour les familles'}
                </Button>
              </div>
            </div>

            {archive.isError ? (
              <p role="alert" className="mt-3 text-sm text-destructive">
                {archive.error.message}
              </p>
            ) : null}
          </td>
        </tr>
      ) : null}

      {/* Archivage = action DISTINCTE, confirmée (jamais un delete silencieux). */}
      <ConfirmDialog
        open={confirmArchive}
        title={`Archiver la compétence ${skill.skillCode} ?`}
        description="L'archivage rend la compétence non proposable, mais conserve ses associations. L'opération est réversible côté serveur (aucune suppression)."
        confirmLabel="Archiver"
        confirmDisabled={archive.isPending}
        onConfirm={onArchive}
        onCancel={() => setConfirmArchive(false)}
      >
        {/* R15-CAT-03 (DBU §2.10 l.2250 amendée DEC-27) — impact « campagnes en cours » : bloc INFORMATIONNEL.
            N'affecte JAMAIS le bouton (confirmDisabled inchangé) : une campagne fige sa version de catalogue,
            l'archivage n'a aucun effet rétroactif. Miroir EXACT du bloc d'archivage profil (S24). */}
        {campaignUsageCount > 0 ? (
          <div className="rounded-md border border-amber-300 bg-amber-50 p-3 dark:border-amber-800 dark:bg-amber-950/30">
            <p className="text-sm font-medium text-foreground">
              Cette compétence est proposable dans {campaignUsageCount} campagne(s) en
              cours. L&apos;archivage n&apos;altère PAS ces campagnes : leur version de
              catalogue est figée (avertissement informationnel).
            </p>
            <ul className="mt-1 list-disc pl-5 text-sm text-muted-foreground">
              {(campaignUsage.data ?? []).map((c) => (
                <li key={c.campaignId}>
                  {c.intitule} ({c.state})
                </li>
              ))}
            </ul>
          </div>
        ) : null}
      </ConfirmDialog>

      {/* Restauration = action DISTINCTE, confirmée (DBU §2.10 l.2256). */}
      <ConfirmDialog
        open={confirmRestore}
        title={`Restaurer la compétence ${skill.skillCode} ?`}
        description="La compétence repasse au statut Actif et redevient proposable aux fournisseurs. Ses associations aux familles sont conservées."
        confirmLabel="Restaurer"
        confirmDisabled={restore.isPending}
        onConfirm={onRestore}
        onCancel={() => setConfirmRestore(false)}
      />

      {restore.isError ? (
        <tr>
          <td colSpan={7} className="px-4 pb-3">
            <p role="alert" className="text-sm text-destructive">
              {restore.error.message}
            </p>
          </td>
        </tr>
      ) : null}
    </>
  );
}
